import React, { useState, useEffect, useCallback } from 'react';
import { t, Trans } from '@lingui/macro';
import moment from 'moment';
import Loader from '../Loader';
import { IMart } from '../../../interfaces/types';
import {
    fetchFootfallTotal,
    fetchYearlyAnalytics,
    ITopDayEntry,
} from '../../../../reducers/api/getReportData';
import {
    getAttendanceZone,
    IReportComparisonRow,
    IReportBreakdownRow,
} from '../../../helpers/reportHelpers';
import { numberWithSpaces } from '../../../helpers/index';
import ReportComparisonChart from './ReportComparisonChart';
import ReportComparisonTable from './ReportComparisonTable';
import ReportGroupedBarChart from './ReportGroupedBarChart';
import ReportTopDaysTable from './ReportTopDaysTable';
import ExportButtons from './ExportButtons';
import { IXlsxTopDay } from '../../../helpers/exportXlsx';

interface IProps {
    mart: IMart;
    referenceDate: moment.Moment;
    yearsBack: number;
}

/** Slovak weekday names, Monday-first */
const WEEKDAY_NAMES = ['Pondelok', 'Utorok', 'Streda', 'Štvrtok', 'Piatok', 'Sobota', 'Nedeľa'];

/** Bi-hourly time periods */
const BIHOURLY_PERIODS = [
    { label: '08:00 – 10:00', startHour: 8, endHour: 10 },
    { label: '10:00 – 12:00', startHour: 10, endHour: 12 },
    { label: '12:00 – 14:00', startHour: 12, endHour: 14 },
    { label: '14:00 – 16:00', startHour: 14, endHour: 16 },
    { label: '16:00 – 18:00', startHour: 16, endHour: 18 },
    { label: '18:00 – 20:00', startHour: 18, endHour: 20 },
    { label: '20:00 – 22:00', startHour: 20, endHour: 22 },
];

const ReportYearly = ({ mart, referenceDate, yearsBack }: IProps) => {
    const [loading, setLoading] = useState(false);
    const [totalRows, setTotalRows] = useState<IReportComparisonRow[]>([]);
    const [monthlyRows, setMonthlyRows] = useState<IReportBreakdownRow[]>([]);
    const [locationRows, setLocationRows] = useState<IReportBreakdownRow[]>([]);
    const [yearColumns, setYearColumns] = useState<{ year: number; label: string }[]>([]);

    // Analytics state — populated from the dedicated yearlyanalytics endpoint
    const [weekendAvgRows, setWeekendAvgRows] = useState<IReportBreakdownRow[]>([]);
    const [weeklyAvgRows, setWeeklyAvgRows] = useState<IReportBreakdownRow[]>([]);
    const [weekdayAvgDailyRows, setWeekdayAvgDailyRows] = useState<IReportBreakdownRow[]>([]);
    const [weekdayAvgRows, setWeekdayAvgRows] = useState<IReportBreakdownRow[]>([]);
    const [bihourlyRows, setBihourlyRows] = useState<IReportBreakdownRow[]>([]);
    const [topDays, setTopDays] = useState<ITopDayEntry[]>([]);

    const refYearStr = referenceDate.format('YYYY');
    const currentYear = referenceDate.year();

    const fetchData = useCallback(async () => {
        setLoading(true);

        const attendanceZone = getAttendanceZone(mart);
        const zoneIds = attendanceZone ? [attendanceZone.id] : [];

        const cols: { year: number; label: string }[] = [];
        for (let y = 0; y <= yearsBack; y++) {
            const yr = referenceDate.clone().subtract(y, 'years').year();
            cols.push({ year: yr, label: String(yr) });
        }
        setYearColumns(cols);

        try {
            // ================================================================
            // 1) Fetch totals per year (simple, one call per year)
            // ================================================================
            const totalPromises: Promise<{ year: number; value: number }>[] = [];
            for (let y = 0; y <= yearsBack; y++) {
                const yr = referenceDate.clone().subtract(y, 'years').year();
                totalPromises.push(
                    fetchFootfallTotal(`${yr}-01-01`, `${yr}-12-31`, zoneIds, [])
                        .then(value => ({ year: yr, value }))
                );
            }

            // 2) Fetch monthly totals per year (one call per month per year)
            const monthNames = [
                t`Január`, t`Február`, t`Marec`, t`Apríl`,
                t`Máj`, t`Jún`, t`Júl`, t`August`,
                t`September`, t`Október`, t`November`, t`December`
            ];
            const monthlyPromises: Promise<{ month: number; year: number; value: number }>[] = [];
            for (let m = 1; m <= 12; m++) {
                const monthKey = String(m).padStart(2, '0');
                for (let y = 0; y <= yearsBack; y++) {
                    const yr = referenceDate.clone().subtract(y, 'years').year();
                    const mStart = `${yr}-${monthKey}-01`;
                    const mEnd = moment(mStart).endOf('month').format('YYYY-MM-DD');
                    monthlyPromises.push(
                        fetchFootfallTotal(mStart, mEnd, zoneIds, [])
                            .then(value => ({ month: m, year: yr, value }))
                    );
                }
            }

            // 3) Fetch yearly analytics from the efficient DB-level endpoint
            const analyticsPromise = fetchYearlyAnalytics(mart.id, currentYear, yearsBack);

            // 4) Fetch by-location totals
            const locPromises: Promise<{ locName: string; values: { year: number; value: number }[] }>[] = [];
            for (const loc of mart.location) {
                const yearPromises: Promise<{ year: number; value: number }>[] = [];
                for (let y = 0; y <= yearsBack; y++) {
                    const yr = referenceDate.clone().subtract(y, 'years').year();
                    yearPromises.push(
                        fetchFootfallTotal(`${yr}-01-01`, `${yr}-12-31`, [], [loc.id])
                            .then(value => ({ year: yr, value }))
                    );
                }
                locPromises.push(
                    Promise.all(yearPromises).then(values => ({ locName: loc.name, values }))
                );
            }

            // Await everything in parallel
            const [totalResults, monthlyResults, analytics, locResults] = await Promise.all([
                Promise.all(totalPromises),
                Promise.all(monthlyPromises),
                analyticsPromise,
                Promise.all(locPromises),
            ]);

            // --- Process total rows ---
            const tRows: IReportComparisonRow[] = totalResults.map(r => ({
                year: r.year,
                label: String(r.year),
                dateFrom: `${r.year}-01-01`,
                dateTo: `${r.year}-12-31`,
                value: r.value,
            }));
            setTotalRows(tRows);

            // --- Process monthly rows ---
            const mRows: IReportBreakdownRow[] = [];
            for (let m = 1; m <= 12; m++) {
                const values = cols.map(col => {
                    const match = monthlyResults.find(r => r.month === m && r.year === col.year);
                    return { year: col.year, value: match ? match.value : 0 };
                });
                mRows.push({ label: monthNames[m - 1], values });
            }
            setMonthlyRows(mRows);

            // --- Process location rows ---
            const lRows: IReportBreakdownRow[] = locResults.map(lr => ({
                label: lr.locName,
                values: lr.values,
            }));
            setLocationRows(lRows);

            // --- Process analytics from the efficient endpoint ---
            if (analytics) {
                // Weekend average
                const wkndRows: IReportBreakdownRow[] = [{
                    label: t`Priemerná návštevnosť víkend (So+Ne)`,
                    values: cols.map(col => {
                        const data = analytics.weekendAvg[String(col.year)];
                        return { year: col.year, value: data ? data.avg_per_weekend : 0 };
                    })
                }];
                setWeekendAvgRows(wkndRows);

                // Weekly average
                const wkRows: IReportBreakdownRow[] = [{
                    label: t`Priemerná návštevnosť za týždeň`,
                    values: cols.map(col => {
                        const data = analytics.weeklyAvg[String(col.year)];
                        return { year: col.year, value: data ? data.avg_per_week : 0 };
                    })
                }];
                setWeeklyAvgRows(wkRows);

                // Weekday (Mon-Fri) daily average
                const wdDailyRows: IReportBreakdownRow[] = [{
                    label: t`Priemerná návštevnosť za pracovný deň (Po-Pi)`,
                    values: cols.map(col => {
                        const data = analytics.weekdayAvgDaily ? analytics.weekdayAvgDaily[String(col.year)] : null;
                        return { year: col.year, value: data ? data.avg_per_weekday : 0 };
                    })
                }];
                setWeekdayAvgDailyRows(wdDailyRows);

                // Weekday averages (Mon-Sun)
                const wdRows: IReportBreakdownRow[] = WEEKDAY_NAMES.map((name, dayIdx) => ({
                    label: name,
                    values: cols.map(col => {
                        const yearData = analytics.weekdayAvg[String(col.year)] || [];
                        // Find matching weekday: our dayIdx 0=Mon→DAYOFWEEK 2, etc.
                        const targetDow = dayIdx === 6 ? 1 : dayIdx + 2; // reverse of mysqlDayOfWeekToIndex
                        const match = yearData.find(wd => wd.weekday === targetDow);
                        return { year: col.year, value: match ? match.avg : 0 };
                    })
                }));
                setWeekdayAvgRows(wdRows);

                // Bi-hourly averages
                const bhRows: IReportBreakdownRow[] = BIHOURLY_PERIODS.map(period => ({
                    label: period.label,
                    values: cols.map(col => {
                        const hourlyData = analytics.bihourlyAvg[String(col.year)] || [];
                        let periodTotal = 0;
                        let periodDayCount = 0;
                        for (let h = period.startHour; h < period.endHour; h++) {
                            const match = hourlyData.find(hd => hd.hour_of_day === h);
                            if (match) {
                                periodTotal += match.total;
                                // Use the max day_count across hours in the period for averaging
                                if (match.day_count > periodDayCount) {
                                    periodDayCount = match.day_count;
                                }
                            }
                        }
                        const avg = periodDayCount > 0 ? Math.round(periodTotal / periodDayCount) : 0;
                        return { year: col.year, value: avg };
                    })
                }));
                setBihourlyRows(bhRows);

                // Top 10 days
                setTopDays(analytics.topDays || []);
            }

        } catch (e) {
            console.error('Error fetching yearly report data', e);
        } finally {
            setLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mart, refYearStr, yearsBack]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Convert totalRows to breakdown format for the table
    const totalBreakdownRows: IReportBreakdownRow[] = totalRows.length > 0
        ? [{ label: t`Celkom`, values: totalRows.map(r => ({ year: r.year, value: r.value })) }]
        : [];

    // KPI summary values (current year)
    const currentYearTotal = totalRows.length > 0 ? totalRows[0].value : 0;
    const currentWeekendAvg = weekendAvgRows.length > 0 && weekendAvgRows[0].values.length > 0
        ? weekendAvgRows[0].values[0].value : 0;
    const currentWeeklyAvg = weeklyAvgRows.length > 0 && weeklyAvgRows[0].values.length > 0
        ? weeklyAvgRows[0].values[0].value : 0;
    const currentWeekdayAvg = weekdayAvgDailyRows.length > 0 && weekdayAvgDailyRows[0].values.length > 0
        ? weekdayAvgDailyRows[0].values[0].value : 0;
    const bestDay = topDays.length > 0 ? topDays[0] : null;

    // Year-over-year % change for total
    const prevYearTotal = totalRows.length > 1 ? totalRows[1].value : 0;
    const yoyChange = prevYearTotal > 0 ? ((currentYearTotal / prevYearTotal) - 1) * 100 : null;

    const buildXlsxInput = () => {
        const xlsxTopDays: IXlsxTopDay[] = topDays.map(d => ({
            date:    moment(d.day_date).format('DD.MM.YYYY'),
            weekday: moment(d.day_date).format('dddd'),
            total:   d.total,
        }));

        return {
            martName:   mart.name,
            reportType: t`Ročný report`,
            dateLabel:  String(currentYear),
            columns:    yearColumns,
            sections: [
                // Order matches the live app's section order
                ...(totalBreakdownRows.length > 0
                    ? [{ title: t`Celková ročná návštevnosť`, rows: totalBreakdownRows, layout: 'total' as const, chartId: 'yearly-total' }]
                    : []),
                ...(weekendAvgRows.length > 0
                    ? [{ title: t`Priemer / víkend (So + Ne)`, rows: weekendAvgRows, layout: 'breakdown' as const }]
                    : []),
                ...(weeklyAvgRows.length > 0
                    ? [{ title: t`Priemer / týždeň`, rows: weeklyAvgRows, layout: 'breakdown' as const }]
                    : []),
                ...(weekdayAvgDailyRows.length > 0
                    ? [{ title: t`Priemer / pracovný deň (Po–Pi)`, rows: weekdayAvgDailyRows, layout: 'breakdown' as const }]
                    : []),
                ...(monthlyRows.length > 0
                    ? [{ title: t`Mesačný prehľad`, rows: monthlyRows, layout: 'breakdown' as const, chartId: 'yearly-monthly' }]
                    : []),
                ...(weekdayAvgRows.length > 0
                    ? [{ title: t`Priemerná denná návštevnosť podľa dňa v týždni`, rows: weekdayAvgRows, layout: 'breakdown' as const, chartId: 'yearly-weekday' }]
                    : []),
                ...(bihourlyRows.length > 0
                    ? [{ title: t`Hodinové priemery (2-hod. intervaly)`, rows: bihourlyRows, layout: 'breakdown' as const, chartId: 'yearly-bihourly' }]
                    : []),
            ],
            // rendered after Top 10 days in the PDF (ignored by XLSX)
            trailingSections: [
                ...(locationRows.length > 0
                    ? [{ title: t`Návštevnosť podľa vstupov`, rows: locationRows, layout: 'breakdown' as const }]
                    : []),
            ],
            kpis: [
                {
                    label: t`Celková návštevnosť`,
                    value: numberWithSpaces(currentYearTotal),
                    sub: yoyChange !== null
                        ? `${yoyChange >= 0 ? '+' : ''}${yoyChange.toFixed(1)}% oproti ${currentYear - 1}`
                        : String(currentYear),
                },
                ...(currentWeekendAvg > 0 ? [{ label: t`Priemer / víkend`, value: numberWithSpaces(currentWeekendAvg), sub: 'Ø So + Ne' }] : []),
                ...(currentWeeklyAvg  > 0 ? [{ label: t`Priemer / týždeň`, value: numberWithSpaces(currentWeeklyAvg),  sub: 'Ø kalendárny týždeň' }] : []),
                ...(currentWeekdayAvg > 0 ? [{ label: t`Priemer / pracovný deň`, value: numberWithSpaces(currentWeekdayAvg), sub: 'Ø Po – Pi' }] : []),
                ...(bestDay ? [{ label: t`Najlepší deň`, value: numberWithSpaces(bestDay.total), sub: moment(bestDay.day_date).format('DD.MM.YYYY') }] : []),
            ],
            topDays: xlsxTopDays.length > 0 ? xlsxTopDays : undefined,
            topDaysTitle: t`Top 10 dní s najvyššou návštevnosťou`,
        };
    };

    /** Inline KPI card */
    const KpiCard = ({ label, value, subtext, accent }: { label: string; value: string; subtext?: string; accent?: boolean }) => (
        <div style={{
            flex: '1 1 0',
            minWidth: 180,
            background: accent ? 'linear-gradient(135deg, #A81A40, #C9375F)' : '#fff',
            color: accent ? '#fff' : '#333',
            borderRadius: 12,
            padding: '20px 18px',
            boxShadow: accent ? '0 4px 16px rgba(168,26,64,0.25)' : '0 1px 6px rgba(0,0,0,0.08)',
            border: accent ? 'none' : '1px solid #eee',
        }}>
            <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', opacity: 0.8, marginBottom: 6 }}>
                {label}
            </div>
            <div style={{ fontSize: 26, fontWeight: 700, lineHeight: 1.1 }}>
                {value}
            </div>
            {subtext && (
                <div style={{ fontSize: 11, marginTop: 6, opacity: 0.7 }}>
                    {subtext}
                </div>
            )}
        </div>
    );

    return (
        <>
            {/* Loading state */}
            {loading && (
                <div className="box mgt-30 mgb-30 pd-40 pdl-25lg pdr-25lg" style={{ position: 'relative', minHeight: 200 }}>
                    <Loader value={true} />
                </div>
            )}

            {!loading && (
                <>
                    {/* ─── Executive Summary KPI Dashboard ─── */}
                    <div className="box mgt-30 mgb-10 pd-40 pdl-25lg pdr-25lg">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                            <h2 className="title_h3" style={{ marginBottom: 0 }}>
                                <Trans>Ročný prehľad</Trans> {currentYear}
                            </h2>
                            <ExportButtons buildInput={buildXlsxInput} />
                        </div>
                        <p className="text_s12 color_grey" style={{ marginBottom: 24 }}>
                            <Trans>Súhrn kľúčových ukazovateľov návštevnosti</Trans>
                        </p>
                        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                            <KpiCard
                                accent
                                label={t`Celková návštevnosť`}
                                value={numberWithSpaces(currentYearTotal)}
                                subtext={yoyChange !== null
                                    ? `${yoyChange >= 0 ? '+' : ''}${yoyChange.toFixed(1)}% ${t`oproti`} ${currentYear - 1}`
                                    : undefined}
                            />
                            <KpiCard
                                label={t`Priemer / víkend`}
                                value={numberWithSpaces(currentWeekendAvg)}
                                subtext={t`Ø So + Ne`}
                            />
                            <KpiCard
                                label={t`Priemer / týždeň`}
                                value={numberWithSpaces(currentWeeklyAvg)}
                                subtext={t`Ø kalendárny týždeň`}
                            />
                            <KpiCard
                                label={t`Priemer / pracovný deň`}
                                value={numberWithSpaces(currentWeekdayAvg)}
                                subtext={t`Ø Po – Pi`}
                            />
                            {bestDay && (
                                <KpiCard
                                    label={t`Najlepší deň`}
                                    value={numberWithSpaces(bestDay.total)}
                                    subtext={moment(bestDay.day_date).format('DD.MM.YYYY')}
                                />
                            )}
                        </div>
                    </div>

                    {/* ─── Section 1: Total yearly footfall ─── */}
                    <div className="box mgt-20 mgb-10 pd-40 pdl-25lg pdr-25lg">
                        <ReportComparisonChart
                            title={t`Celková ročná návštevnosť`}
                            rows={totalRows}
                            chartId="yearly-total"
                        />
                        <ReportComparisonTable
                            title={t`Celková ročná návštevnosť`}
                            columns={yearColumns}
                            rows={totalBreakdownRows}
                        />
                    </div>

                    {/* ─── Section 2: Average footfall per weekend ─── */}
                    {weekendAvgRows.length > 0 && (
                        <div className="box mgt-20 mgb-10 pd-40 pdl-25lg pdr-25lg">
                            <ReportComparisonTable
                                title={t`Priemerná návštevnosť za víkend (So + Ne)`}
                                subtitle={t`Priemerný počet návštevníkov na jeden víkend (sobota + nedeľa)`}
                                columns={yearColumns}
                                rows={weekendAvgRows}
                            />
                        </div>
                    )}

                    {/* ─── Section 3: Average footfall per week ─── */}
                    {weeklyAvgRows.length > 0 && (
                        <div className="box mgt-20 mgb-10 pd-40 pdl-25lg pdr-25lg">
                            <ReportComparisonTable
                                title={t`Priemerná návštevnosť za týždeň`}
                                subtitle={t`Priemerný počet návštevníkov na jeden kalendárny týždeň`}
                                columns={yearColumns}
                                rows={weeklyAvgRows}
                            />
                        </div>
                    )}

                    {/* ─── Section 3b: Average footfall per weekday (Mon-Fri) ─── */}
                    {weekdayAvgDailyRows.length > 0 && (
                        <div className="box mgt-20 mgb-10 pd-40 pdl-25lg pdr-25lg">
                            <ReportComparisonTable
                                title={t`Priemerná návštevnosť za pracovný deň`}
                                subtitle={t`Priemerný počet návštevníkov na jeden pracovný deň (pondelok – piatok)`}
                                columns={yearColumns}
                                rows={weekdayAvgDailyRows}
                            />
                        </div>
                    )}

                    {/* ─── Section 4: Monthly breakdown with year-over-year comparison ─── */}
                    {monthlyRows.length > 0 && (
                        <div className="box mgt-20 mgb-10 pd-40 pdl-25lg pdr-25lg">
                            <ReportGroupedBarChart
                                title={t`Mesačný prehľad`}
                                columns={yearColumns}
                                rows={monthlyRows}
                                height={500}
                                chartId="yearly-monthly"
                            />
                            <ReportComparisonTable
                                title={t`Mesačný prehľad`}
                                subtitle={t`Porovnanie návštevnosti po mesiacoch s predchádzajúcimi rokmi`}
                                columns={yearColumns}
                                rows={monthlyRows}
                            />
                        </div>
                    )}

                    {/* ─── Section 5: Daily average by weekday ─── */}
                    {weekdayAvgRows.length > 0 && (
                        <div className="box mgt-20 mgb-10 pd-40 pdl-25lg pdr-25lg">
                            <ReportGroupedBarChart
                                title={t`Priemerná denná návštevnosť podľa dňa v týždni`}
                                columns={yearColumns}
                                rows={weekdayAvgRows}
                                height={450}
                                chartId="yearly-weekday"
                            />
                            <ReportComparisonTable
                                title={t`Priemerná denná návštevnosť podľa dňa v týždni`}
                                subtitle={t`Priemer za každý deň v týždni (pondelok – nedeľa)`}
                                columns={yearColumns}
                                rows={weekdayAvgRows}
                            />
                        </div>
                    )}

                    {/* ─── Section 6: Bi-hourly (2-hour) averages ─── */}
                    {bihourlyRows.length > 0 && (
                        <div className="box mgt-20 mgb-10 pd-40 pdl-25lg pdr-25lg">
                            <ReportGroupedBarChart
                                title={t`Priemerná hodinová návštevnosť (2-hodinové intervaly)`}
                                columns={yearColumns}
                                rows={bihourlyRows}
                                height={450}
                                chartId="yearly-bihourly"
                            />
                            <ReportComparisonTable
                                title={t`Priemerná hodinová návštevnosť (2-hodinové intervaly)`}
                                subtitle={t`Priemerný denný počet návštevníkov v dvojhodinových pásmach`}
                                columns={yearColumns}
                                rows={bihourlyRows}
                            />
                        </div>
                    )}

                    {/* ─── Section 7: Top 10 days with highest footfall (current year) ─── */}
                    {topDays.length > 0 && (
                        <div className="box mgt-20 mgb-10 pd-40 pdl-25lg pdr-25lg">
                            <ReportTopDaysTable
                                title={t`Top 10 dní s najvyššou návštevnosťou`}
                                subtitle={`${currentYear}`}
                                days={topDays}
                            />
                        </div>
                    )}

                    {/* ─── Section 8: By Location ─── */}
                    {locationRows.length > 0 && (
                        <div className="box mgt-20 mgb-30 pd-40 pdl-25lg pdr-25lg">
                            <ReportComparisonTable
                                title={t`Návštevnosť podľa vstupov`}
                                columns={yearColumns}
                                rows={locationRows}
                            />
                        </div>
                    )}
                </>
            )}
        </>
    );
};

export default ReportYearly;
